import { usePage } from "@inertiajs/react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import HttpStatus from "http-status-codes";
import { FormEvent, useState } from "react";

import Input, { ChangeHandler, InputType } from "../components/forms/Input";
import JourneyCost from "../components/forms/JourneyCost";
import SignInForm from "../components/forms/SignInForm";
import ModalContent from "../components/modal/ModalContent";
import { SharedModalProps } from "../components/modal/ModalContextProvider";
import useFetch, { FailureHandler } from "../hooks/useFetch";
import useModalDispatch from "../hooks/useModalDispatch";
import { parseFormErrors } from "../lib/form";

import CompensationSuccessModal from "./CompensationSuccessModal";

const initialValues = {
  description: "",
  from: "",
  to: "",
  price: "",
  user_name: "",
};

const DISCOUNT_OPTIONS = [
  { value: "1", label: "Nee" },
  { value: "0.8", label: "20%" },
  { value: "0.6", label: "40%" },
];

const CompensationRequestModal = ({
  eventId,
  handleClose,
  onSuccess,
}: SharedModalProps<{
  eventId: string;
  onSuccess: () => void;
}>) => {
  const [loginForm, setLoginForm] = useState(false);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const dispatchSuccessModal = useModalDispatch(CompensationSuccessModal);
  const handleSuccess = () => {
    onSuccess();
    dispatchSuccessModal();
  };
  const handleFailure: FailureHandler = ({ json, response }) => {
    if (response?.status === HttpStatus.UNAUTHORIZED) {
      setLoginForm(true);
    } else if (response?.status === HttpStatus.UNPROCESSABLE_ENTITY) {
      setErrors(parseFormErrors(json, Object.keys(values)));
    }
  };
  const { baseUrl } = usePage().props;
  const [post, { error, loading }] = useFetch(
    `${baseUrl}/events/${eventId}/compensations`,
    { method: "POST", onFailure: handleFailure, onSuccess: handleSuccess },
  );

  const [discount, setDiscount] = useState("1");
  const [fromStationId, setFromStationId] = useState<undefined | string>(
    undefined,
  );

  const [toStationId, setToStationId] = useState<undefined | string>(undefined);

  const submitForm = () =>
    post({
      compensation: values,
    });
  if (loginForm) {
    return (
      <ModalContent label="Inloggen" onClose={handleClose}>
        <Stack spacing={1}>
          <div>{errors.unauthorized}</div>
          <SignInForm
            onSuccess={() => {
              submitForm();
              setLoginForm(false);
            }}
          />
        </Stack>
      </ModalContent>
    );
  }
  const handleChange: ChangeHandler = (e) => {
    const { name, value } = e.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
    if (name !== "price") {
      setErrors({});
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e?.preventDefault();
    submitForm();
  };

  return (
    <ModalContent label="Vergoeding aanvragen" onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <Stack spacing="1em" sx={{ marginBottom: "2em" }}>
          <Typography>
            Met dit formulier kan je reistkostenvergoeding aanvragen voor deze
            demonstratie.
          </Typography>
          <Typography>
            Na afloop van de demonstratie sturen we je een link om een bewijs
            van je deelname te uploaden. Dit kan een foto van een los
            treinkaartje of een declaratieoverzicht van je OV-chipkaart zijn.
          </Typography>
          <Typography>
            Je geeft dan ook jouw IBAN rekeningnummer door zodat de sponsor het
            bedrag kan overmaken. <strong>Let op:</strong> je kan je
            rekeningnummer maar 1 keer per demonstratie gebruiken.
          </Typography>
          <Typography>
            Deze gegevens worden naar de sponsor gestuurd zodat diegene het
            bedrag kan overmaken. Na afloop verwijderen wij alle gegevens.
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <Input
            autoFocus
            required
            error={errors.user_name?.[0]}
            label="Hoe wil je genoemd worden?"
            name="user_name"
            placeholder="Naam"
            type={InputType.Text}
            value={values.user_name}
            onChange={handleChange}
          />
          <Input
            error={errors.description?.[0]}
            label="Waarom wil je naar de demonstratie?"
            name="description"
            placeholder="Schrijf een korte toelichting (optioneel)"
            type={InputType.TextArea}
            value={values.description}
            onChange={handleChange}
          />
          <Input
            label="Reis je met korting?"
            name="discount"
            options={DISCOUNT_OPTIONS}
            sx={{ flexDirection: "row" }}
            type={InputType.RadioGroup}
            value={discount}
            onChange={(e) => setDiscount(e.target.value as string)}
          />
          <Input
            required
            error={errors.from?.[0]}
            label="Waar reis je vandaan?"
            name="from"
            placeholder="Van"
            type={InputType.Station}
            value={values.from}
            onChange={(e) => {
              handleChange(e);
              setFromStationId((e.target as any).stationId);
            }}
          />
          <Input
            required
            error={errors.to?.[0]}
            label="Waar reis je naartoe?"
            name="to"
            placeholder="Naar"
            type={InputType.Station}
            value={values.to}
            onChange={(e) => {
              handleChange(e);
              setToStationId(e.target.stationId);
            }}
          />
          <JourneyCost
            discount={discount}
            from={fromStationId}
            name="price"
            to={toStationId}
            onChange={handleChange}
          />
          <Typography marginTop="1em!important" variant="body2">
            Solidaritijd probeert je te verbinden met iemand die jouw reiskosten
            wil vergoeden. Dit regelen jullie vervolgens onderling. We kunnen
            niet garanderen dat dat lukt.{" "}
            <a
              href="https://solidaritijd.nl/over"
              rel="noreferrer"
              target="_blank"
            >
              Lees meer
            </a>
            .
          </Typography>
          <Typography marginTop="1em!important" variant="body2">
            Je mag maar 1 keer compensatie aanvragen per demonstratie.
          </Typography>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ marginTop: "1em" }}>
            {error}
            <ul>
              {errors.general?.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}
        <Stack direction="row" justifyContent="end">
          <Button
            loading={loading}
            sx={{ marginTop: "1em" }}
            type="submit"
            variant="contained"
          >
            Verstuur
          </Button>
        </Stack>
      </form>
    </ModalContent>
  );
};

export default CompensationRequestModal;
